/**
 * Extracts query parameters from a search string and adds them to a URLSearchParams object.
 * The search string can include or exclude the '?' prefix.
 *
 * @param search - The search string (e.g., "?key=value" or "key=value")
 * @param targetParams - The URLSearchParams object to add the extracted params to
 */
export const extractQueryParams = (search: string, targetParams: URLSearchParams): void => {
  if (search && search.length > 1) {
    const searchParams = new URLSearchParams(search)
    searchParams.forEach((value, key) => {
      targetParams.set(key, value)
    })
  }
}

/**
 * Creates a new URLSearchParams object with all params from source except the excluded keys.
 *
 * @param sourceParams - The source URLSearchParams to copy from
 * @param excludeKeys - Array of keys to exclude from the result
 * @returns A new URLSearchParams object with the filtered params
 */
export const preserveQueryParams = (
  sourceParams: URLSearchParams,
  excludeKeys: string[] = [],
): URLSearchParams => {
  const params = new URLSearchParams()
  const excludeSet = new Set(excludeKeys)

  sourceParams.forEach((value, key) => {
    if (!excludeSet.has(key)) {
      params.set(key, value)
    }
  })

  return params
}

/**
 * Builds a full URL by combining a pathname with query parameters.
 *
 * @param pathname - The pathname (e.g., "/recognitions/123")
 * @param queryParams - The query parameters to append
 * @returns The full URL with query string (e.g., "/recognitions/123?key=value")
 */
export const buildUrlWithQueryParams = (pathname: string, queryParams: URLSearchParams): string => {
  const queryString = queryParams.toString()
  return queryString ? `${pathname}?${queryString}` : pathname
}
